import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import filter from 'lodash/filter'
import some from 'lodash/some'
import Popup from '@SiteComponents/Popup'
import Button from '@SiteComponents/Button'
import getBulkExportationInfo from '@Utils/GetBulkExportationInfo'
import SelectedProjectsTable from './SelectedProjectsTable'
import FormatSelection from './FormatSelection'
import ContentToExport from './ContentToExport'
import LanguageSelection from './LanguageSelection'
import Options from './Options'
import Cancel from '@Images/new_project_popup_cancel.svg'

import {
  closeDashboardPopups,
} from '@SiteModules/Popup/actions'

import {
  startBulkExportation,
} from '@SiteModules/Project/actions'

import {
  getEditingProjectIds,
  getEditingProjects,
  getAtLeastOneEditingProjectHasClips,
  getAllLanguagesOfEditingProjects,
} from '@SiteModules/Popup/selectors'

import {
  getStartingBulkExportation,
} from '@SiteModules/Project/selectors'

const FORMATS = [
  'Video (.mp4)',
  'Video (.mov)',
  'Audio (.mp3)',
  'Audio (.wav)',
  'Subtitles (.srt)',
  'Subtitles (.vtt)',
  'Subtitles (.ttml)',
  'Subtitles (.csv)',
  'Subtitles (.avid)',
  'Subtitles (.itt)',
  'Subtitles (.txt)',
  'Subtitles (.teletext)'
]

const CONTENT = [
  'Original videos',
  'All clips'
]

import './ExportationPopup.scss'

const ExportationPopup: React.FC = () => {
  const dispatch = useDispatch()
  const editingProjectIds = useSelector(getEditingProjectIds)
  const editingProjects = useSelector(getEditingProjects)
  const atLeastOneEditingProjectHasClips = useSelector(getAtLeastOneEditingProjectHasClips)
  const availableLanguages = useSelector(getAllLanguagesOfEditingProjects)
  const startingBulkExportation = useSelector(getStartingBulkExportation)

  const [availableContent, setAvailableContent] = useState(atLeastOneEditingProjectHasClips ? CONTENT : [CONTENT[0]])
  const [selectedFormats, setSelectedFormats] = useState([FORMATS[0]])
  const [contentToExport, setContentToExport] = useState(availableContent)
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
  const [shouldBurnInSubtitles, setShouldBurnInSubtitles] = useState(true)
  const [shouldHaveOneAudioFilePerSpeaker, setShouldHaveOneAudioFilePerSpeaker] = useState(false)

  const willExportOriginalVideos = contentToExport.includes('Original videos')
  const willExportAllClips = contentToExport.includes('All clips')

  const audioFormatSelected = some(selectedFormats, selectedFormat => selectedFormat.includes('Audio'))
  const videoFormatSelected = some(selectedFormats, selectedFormat => selectedFormat.includes('Video'))

  const bulkExportationInfo = getBulkExportationInfo(editingProjects, willExportOriginalVideos, willExportAllClips, selectedLanguages, selectedFormats, shouldHaveOneAudioFilePerSpeaker)
  const estimatedNumberOfExportationFiles = bulkExportationInfo.estimatedNumberOfExportationFiles
  const hasNoExportationFiles = estimatedNumberOfExportationFiles === 0

  useEffect(() => {
    if (!atLeastOneEditingProjectHasClips) {
      setAvailableContent([CONTENT[0]])

      if (contentToExport.includes(CONTENT[1])) {
        setContentToExport(filter(contentToExport, content => content !== CONTENT[1]))
      }
    }
  }, [atLeastOneEditingProjectHasClips])

  useEffect(() => {
    if (!videoFormatSelected && shouldBurnInSubtitles) {
      setShouldBurnInSubtitles(false)
    }

    if (videoFormatSelected && !shouldBurnInSubtitles) {
      setShouldBurnInSubtitles(true)
    }
  }, [videoFormatSelected])

  useEffect(() => {
    if (!audioFormatSelected && shouldHaveOneAudioFilePerSpeaker) {
      setShouldHaveOneAudioFilePerSpeaker(false)
    }
  }, [audioFormatSelected])

  const handleClose = () => {
    dispatch(closeDashboardPopups())
  }

  const handleStartBulkExportation = () => {
    const {
      projectVersionIdsToExportWithoutSubtitles,
      subtitleIdsToExportAsSubtitles,
      subtitleIdsToExportAsAudiosAndVideos
    } = bulkExportationInfo

    dispatch(startBulkExportation({ selectedFormats, shouldHaveOneAudioFilePerSpeaker, shouldBurnInSubtitles, projectVersionIdsToExportWithoutSubtitles, subtitleIdsToExportAsSubtitles, subtitleIdsToExportAsAudiosAndVideos }))
  }

  const renderSelectedProjects = () => {
    return (
      <div className="ExportationPopup__section">
        <div className="ExportationPopup__section-header">
          Selected projects
        </div>
        <SelectedProjectsTable
          editingProjects={editingProjects}
          editingProjectIds={editingProjectIds}
          closePopup={handleClose}
        />
      </div>
    )
  }

  const renderFormat = () => {
    return (
      <div className="ExportationPopup__section">
        <div className="ExportationPopup__section-header">
          Format selection
        </div>
        <FormatSelection
          selectedFormats={selectedFormats}
          setSelectedFormats={setSelectedFormats}
          availableFormats={FORMATS}
        />
      </div>
    )
  }

  const renderContent = () => {
    return (
      <div className="ExportationPopup__section">
        <div className="ExportationPopup__section-header">
          Content to export
        </div>
        <ContentToExport
          availableContent={availableContent}
          contentToExport={contentToExport}
          setContentToExport={setContentToExport}
        />
      </div>
    )
  }

  const renderLanguages = () => {
    return (
      <div className="ExportationPopup__section">
        <div className="ExportationPopup__section-header">
          Select languages
        </div>
        <LanguageSelection
          availableLanguages={availableLanguages}
          selectedLanguages={selectedLanguages}
          setSelectedLanguages={setSelectedLanguages}
        />
      </div>
    )
  }

  const renderOptions = () => {
    return (
      <div className="ExportationPopup__section">
        <div className="ExportationPopup__section-header">
          Options
        </div>
        <Options
          shouldBurnInSubtitles={shouldBurnInSubtitles}
          setShouldBurnInSubtitles={setShouldBurnInSubtitles}
          shouldHaveOneAudioFilePerSpeaker={shouldHaveOneAudioFilePerSpeaker}
          setShouldHaveOneAudioFilePerSpeaker={setShouldHaveOneAudioFilePerSpeaker}
          audioFormatSelected={audioFormatSelected}
          videoFormatSelected={videoFormatSelected}
        />
      </div>
    )
  }

  const renderBottom = () => {
    return (
      <div className="ExportationPopup__bottom">
        <div className="ExportationPopup__bottom-left">
          <Button
            caption="Cancel"
            icon={<Cancel />}
            onClick={handleClose}
            styleType="gray-subtle"
            size="small"
          />
          <div className="ExportationPopup__bottom-estimated-files">
            Estimated files: {estimatedNumberOfExportationFiles}
          </div>
        </div>
        <Button
          caption="Start Export"
          styleType="brand"
          size="small"
          onClick={handleStartBulkExportation}
          disabled={hasNoExportationFiles || startingBulkExportation}
        />
      </div>
    )
  }

  return (
    <Popup
      className="ExportationPopup"
      zIndex={200000}
      closePopup={handleClose}
    >
      <div className="ExportationPopup__header">
        Export project assets
      </div>
      {renderSelectedProjects()}
      {renderFormat()}
      {renderContent()}
      {renderLanguages()}
      {renderOptions()}
      {renderBottom()}
    </Popup>
  )
}

export default ExportationPopup
